// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

// Material UI
import { ThemeProvider } from '@mui/material';

// Custom Layouts
import BaseLayout            from './layouts/BaseLayout';
import DashboardLayout       from './layouts/DashboardLayout';
import TestPage              from './layouts/TestPage';

// Custom Components
import Login                 from './components/Login';
import AdminDashboard        from './components/AdminDashboard';
import Dashboard             from './components/Dashboard';
import StudentDashboard      from './components/StudentDashboard';
import StudentActivity       from './components/StudentActivity';
import VideoPage             from './components/Video';
import VideoPageV2           from './layouts/VideoV2';

// Custom Styles
import './index.css';
import { customTheme } from './theme';

// Tracing Scripts
import * as Sentry from "@sentry/react";
import reportWebVitals from './reportWebVitals';




// ----------------------------------------------------------------------------
//   Initialization: Sentry Error Tracing
// ----------------------------------------------------------------------------


Sentry.init({
  dsn: "https://2ae8862bcb414c52bd2b6549eac19305@o353488.ingest.us.sentry.io/4508179161022464",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation, useNavigationType, createRoutesFromChildren, matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],

  // Set tracing environment from env var
  environment: process.env.REACT_APP_ENV || null,

  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/dev\.fontanpractice\.northwestern\.edu/,
    /^https:\/\/fontanpractice\.northwestern\.edu/,
  ],

  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});



// ----------------------------------------------------------------------------
//   Initialization: React Application
// ----------------------------------------------------------------------------


// Create React root
const root = ReactDOM.createRoot(document.getElementById('root'));

// Set up router
const router = createBrowserRouter([
  {
    path: "/login",
    element: <BaseLayout><Login /></BaseLayout>,
  },
  {
    path: "/profile",
    element: <BaseLayout><Dashboard /></BaseLayout>,
  },
  {
    path: "/student",
    element: (<BaseLayout><DashboardLayout><StudentDashboard/></DashboardLayout></BaseLayout>),
  },
  {
    path: "/admin/:studentId",
    element: (<BaseLayout><DashboardLayout><StudentActivity/></DashboardLayout></BaseLayout>),
  },
  {
    path: "/admin",
    element: (<BaseLayout><DashboardLayout><AdminDashboard /></DashboardLayout></BaseLayout>),
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    path: "/video/:id",
    element: (<BaseLayout scrollable={false}><VideoPageV2 step={1} /></BaseLayout>),
  },

  // When running locally, add a test page route for developing individual components
  (
    process.env.REACT_APP_ENV !== 'local' ? null :
      {
        path: "/test-page",
        element: (<TestPage />),
      }
  ),
].filter(x => x !== null));

// Render the React app
root.render(
  <React.StrictMode>
    <ThemeProvider theme={customTheme}>
      <RouterProvider router={router} />
    </ThemeProvider>
  </React.StrictMode>
);



// ----------------------------------------------------------------------------
//   Initialization: React App Performance
// ----------------------------------------------------------------------------


// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
